import React, { ReactElement, Fragment, useEffect, useState, useMemo } from "react";
import { X } from "lucide-react";
import { gql, useQuery } from "urql";
import { Formik } from "formik";
import { cloneDeep } from "lodash";

import { DateHelper } from "../../../common/src";
import { SearchListField } from "../../../components/src/form";
import { useQueryState, useQueryStateArray } from "../../../components/src/use-query-state";
import { PeriodSelector } from "../components/PeriodSelector";
import { Grid, Column } from "../../../components/src";
import { DateForm } from "../../../components/src/dateForm";
import { TimeSeriesOperationPlot } from "./timeseries/TimeSeriesOperationPlot";
import { CurrencyEnum, InstrumentModelTypeEnum, TimeSeries } from "../types.generated";

interface DataProp {
    _id: string;
    instrumentId: string;
    type: string;
    instrument: {
        name: string;
        longName: string;
        currency: CurrencyEnum;
        modelType: InstrumentModelTypeEnum;
        bloombergTicker: string;
        isin: string;
    };
}

interface CompareInstrument {
    _id: string;
    name: string;
    longName: string;
    currency: CurrencyEnum;
    modelType: InstrumentModelTypeEnum;
    type: string;
    exchangedCurrency: CurrencyEnum;
    fxHedged: boolean;
    index: number;
}

const GET_DATA = gql`
    query {
        timeseries(filter: { statusIn: Active, typeIn: ["Price(Close)", "Return(Total)"] }) {
            _id
            instrumentId
            type
            instrument {
                modelType
                name
                longName
                currency
                bloombergTicker
                isin
            }
        }
    }
`;

const DEFAULT_CURRENCY = CurrencyEnum.SEK;

const DERIVATIVE_MODEL_TYPES = {
    [InstrumentModelTypeEnum.Swap]: true,
    [InstrumentModelTypeEnum.Swaption]: true,
    [InstrumentModelTypeEnum.FxSwap]: true
};

const Checkbox = (props) => <input type="checkbox" {...props} />;

/** Legacy compare page preserved for gradual migration; master uses ComparePage at /compare. */
export function ComparePage2(): ReactElement {
    const [rebase, setRebase] = useQueryState("rebase", "true");
    const [alignDateRange, setAlignDateRange] = useQueryState("align", "true");
    const [useTotalReturn, setUseTotalReturn] = useQueryState("utr", "false");

    const [startDate] = useQueryState("startDate", DateHelper.addYears(new Date(), -1).toISOString().slice(0, 10));
    const [endDate] = useQueryState("endDate", new Date().toISOString().slice(0, 10));

    const [currency, setCurrency] = useQueryState("currency", "");

    const [timeserieInstrumentIds, setTimeserieInstrumentIds] = useQueryStateArray("ts", []);
    const [exchange, setExchange] = useQueryStateArray("ex", []);
    const [fxHedged, setFxHedged] = useQueryStateArray("fx", []);

    const [instruments, setInstruments] = useState([] as CompareInstrument[]);
    const [globalCurrency, setGlobalCurrency] = useState(DEFAULT_CURRENCY);

    const addTypeInName = false;
    const useRangeSelector = false;
    const margin = { t: 0, b: 0, l: 0, r: 200, pad: 0 };

    const [{ data, fetching: loading, error }] = useQuery({
        query: GET_DATA,
        requestPolicy: "cache-and-network"
    });

    useEffect(() => {
        const ids = instruments && instruments.length > 0 ? instruments.map((i) => i._id) : [];
        if (data && data.timeseries && ids.toString() != timeserieInstrumentIds.toString()) {
            const updatedInstruments = timeserieInstrumentIds.map((instrumentId: string, index: number) => {
                const existingInstrument = instruments.find((x) => x._id == instrumentId);
                if (existingInstrument) {
                    const instr = cloneDeep(existingInstrument);
                    instr.index = index;
                    return instr;
                } else {
                    const ts = data.timeseries.find((t: DataProp) => t._id == instrumentId) as TimeSeries;
                    return {
                        _id: ts._id,
                        name: ts.instrument.name,
                        longName: ts.instrument.longName,
                        currency: ts.instrument.currency,
                        modelType: ts.instrument.modelType,
                        type: ts.type,
                        exchangedCurrency: currency
                            ? globalCurrency
                            : exchange.length > index
                              ? (exchange[index] as CurrencyEnum)
                              : ts.instrument.currency,
                        fxHedged: fxHedged.length > index ? JSON.parse(fxHedged[index]) : false,
                        index
                    };
                }
            });
            setInstruments(updatedInstruments as CompareInstrument[]);
            const exchangedCurrencies = updatedInstruments.map((x) => x.exchangedCurrency);
            setExchange(exchangedCurrencies);
            const fx = updatedInstruments.map((x) => x.fxHedged);
            setFxHedged(fx);
        }
    }, [instruments, timeserieInstrumentIds, data, currency, globalCurrency, exchange, setExchange, fxHedged, setFxHedged]);

    const options = useMemo(() => {
        let options = [];
        if (data && data.timeseries) {
            if (useTotalReturn === "true") {
                const localOptions = [];
                for (const t of data.timeseries as DataProp[]) {
                    if (
                        t.type === "Return(Total)" &&
                        t.instrument &&
                        t.instrument.modelType &&
                        !DERIVATIVE_MODEL_TYPES[t.instrument.modelType]
                    ) {
                        localOptions.push({
                            _id: t._id,
                            name: t.instrument.name,
                            longName: t.instrument.longName,
                            isin: t.instrument.isin,
                            currency: t.instrument.currency,
                            bloombergTicker: t.instrument.bloombergTicker
                        });
                    }
                }
                options = localOptions;
            } else {
                const localOptions = [];
                for (const t of data.timeseries as DataProp[]) {
                    if (t.type === "Price(Close)") {
                        localOptions.push({
                            _id: t._id,
                            name: t.instrument.name,
                            longName: t.instrument.longName,
                            isin: t.instrument.isin,
                            currency: t.instrument.currency,
                            bloombergTicker: t.instrument.bloombergTicker
                        });
                    }
                }
                options = localOptions;
            }
        }
        return options;
    }, [data, useTotalReturn]);

    if (loading)
        return (
            <div>
                <p>Loading...</p>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    const handleChange = (e) => {
        if (e) {
            const newIds = timeserieInstrumentIds.slice();
            newIds.push(e);
            setTimeserieInstrumentIds(newIds);
        }
    };

    const handleCurrencyCheckChange = (e) => {
        if (e.target.checked) {
            setCurrency(globalCurrency); // set query param
            setExchange([]);
            setFxHedged([]);
            if (instruments && instruments.length > 0) {
                const updatedInstruments = instruments.map((i) => {
                    const instr = cloneDeep(i);
                    instr.exchangedCurrency = globalCurrency;
                    return instr;
                });
                setInstruments(updatedInstruments);
            }
        } else {
            setCurrency("");
            if (instruments && instruments.length > 0) {
                const updatedInstruments = instruments.map((i) => {
                    const instr = cloneDeep(i);
                    instr.exchangedCurrency = instr.currency;
                    return instr;
                });
                setInstruments(updatedInstruments);
                const exchangedCurrencies = updatedInstruments.map((x) => x.exchangedCurrency);
                setExchange(exchangedCurrencies);
                const fx = updatedInstruments.map((x) => x.fxHedged.toString());
                setFxHedged(fx);
            }
        }
    };

    const handleGlobalCurrencyChange = (e) => {
        setCurrency(e.target.value);
        setGlobalCurrency(e.target.value);
        if (instruments && instruments.length > 0) {
            const updatedInstruments = instruments.map((i) => {
                const instr = cloneDeep(i);
                instr.exchangedCurrency = e.target.value as CurrencyEnum;
                return instr;
            });
            setInstruments(updatedInstruments);
        }
    };

    const handleRebase = (e) => {
        if (e.target.checked) {
            setRebase("true");
        } else {
            setRebase("false");
        }
    };

    const handleAlignDateRange = (e) => {
        if (e.target.checked) {
            setAlignDateRange("true");
        } else {
            setAlignDateRange("false");
        }
    };

    const handleUseTotalReturn = (e) => {
        if (e.target.checked) {
            setUseTotalReturn("true");
        } else {
            setUseTotalReturn("false");
        }
        // changed so remove all instruments
        setTimeserieInstrumentIds([]);
    };

    const uniqueCurrencies: string[] = [...new Set<string>(options.map((item: any) => item.currency))];

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full lg:w-1/3 md:w-1/2 px-2 mb-4">
                    <Formik
                        key={timeserieInstrumentIds.join(",")}
                        enableReinitialize={true}
                        initialValues={{ timeserieId: null }}
                        onSubmit={null}
                    >
                        {() => (
                            <Fragment>
                                <div className="flex flex-wrap -mx-2">
                                    <SearchListField
                                        name="instrumentId"
                                        label="Add timeseries"
                                        className="w-full px-2 mb-4"
                                        options={options}
                                        disabled={false}
                                        onChange={handleChange}
                                    />
                                </div>
                            </Fragment>
                        )}
                    </Formik>
                    <div>
                        <Checkbox checked={rebase === "true"} onChange={handleRebase} disabled={timeserieInstrumentIds.length === 0} />{" "}
                        <span>Rebase series</span>
                    </div>
                    <div>
                        <Checkbox
                            checked={alignDateRange === "true"}
                            onChange={handleAlignDateRange}
                            disabled={timeserieInstrumentIds.length === 0}
                        />{" "}
                        <span>Align date range</span>
                    </div>
                    <div>
                        <Checkbox checked={currency} onChange={handleCurrencyCheckChange} disabled={timeserieInstrumentIds.length === 0} />{" "}
                        <span>Same currency</span>
                        <select
                            id="currency"
                            className="ml-1"
                            defaultValue={DEFAULT_CURRENCY}
                            disabled={!currency}
                            onChange={handleGlobalCurrencyChange}
                        >
                            {uniqueCurrencies.map((d: string, i) => (
                                <option key={i} value={d}>
                                    {d}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Checkbox checked={useTotalReturn === "true"} onChange={handleUseTotalReturn} disabled={false} />{" "}
                        <span>Total return</span>
                    </div>
                </div>
                <div className="hidden lg:block lg:w-1/12 md:w-1/12 px-2 mb-4"></div>
                <div className="w-full lg:w-1/3 md:w-5/12 px-2 mb-4">
                    <PeriodSelector firstAvailableStartDate={""} />
                    <DateForm className="width-fixed" defaultDateString={startDate} dateName={"startDate"}></DateForm>
                    <DateForm className="width-fixed" defaultDateString={endDate} dateName={"endDate"}></DateForm>
                </div>
                <div className="hidden lg:block lg:w-1/4 md:hidden px-2 mb-4"></div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <Grid data={instruments} className="table-timeseries" tableClassName="table-xs">
                        <Column field="name" className="nowrap left" />
                        <Column field="longName" className="nowrap left" />
                        <Column field="currency" title="Instr. ccy" />
                        <Column
                            field="exchangedCurrency"
                            title="Calc. ccy"
                            format={(value, item) => (
                                <select
                                    id="currency"
                                    data-ndx={item.index}
                                    className="ml-1"
                                    disabled={!!currency}
                                    value={value}
                                    onChange={(e) => {
                                        const updatedInstruments = cloneDeep(instruments);
                                        const instrIndex = e.target.dataset["ndx"];
                                        const instr = updatedInstruments[instrIndex];
                                        instr.exchangedCurrency = e.target.value as CurrencyEnum;
                                        setInstruments(updatedInstruments);
                                        const exchangedCurrencies = updatedInstruments.map((x) => x.exchangedCurrency);
                                        setExchange(exchangedCurrencies);
                                    }}
                                >
                                    {uniqueCurrencies.map((d: string, i) => (
                                        <option key={i} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                            )}
                        />
                        <Column
                            className="center"
                            field="Remove"
                            format={() => (
                                <div style={{ width: "16px", height: "16px" }}>
                                    <X size={16} />
                                </div>
                            )}
                            onClick={(e, i) => {
                                const updatedInstruments = instruments.filter((instr) => instr.index != i);
                                const tsIds = updatedInstruments.map((instr) => instr._id);
                                setTimeserieInstrumentIds(tsIds);
                            }}
                        />
                        <Column
                            field="fxHedged"
                            format={(value, item) => (
                                <input
                                    type="checkbox"
                                    data-ndx={item.index}
                                    checked={value}
                                    disabled={!instruments}
                                    onChange={(e) => {
                                        const updatedInstruments = cloneDeep(instruments);
                                        const instrIndex = e.target.dataset["ndx"];
                                        const instr = updatedInstruments[instrIndex];
                                        instr.fxHedged = e.target.checked;
                                        setInstruments(updatedInstruments);
                                        const fx = updatedInstruments.map((x) => x.fxHedged.toString());
                                        setFxHedged(fx);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                {instruments && instruments.length > 0 ? (
                    <div className="w-full px-2 mb-4">
                        <TimeSeriesOperationPlot
                            params={instruments}
                            startDate={startDate}
                            endDate={endDate}
                            alignDateRange={alignDateRange === "true"}
                            rebase={rebase === "true"}
                            addTypeInName={addTypeInName}
                            useRangeSelector={useRangeSelector}
                            customMargin={margin}
                            totalReturn={useTotalReturn === "true"}
                        />
                    </div>
                ) : null}
            </div>
        </Fragment>
    );
}
